import { Fragment, useRef } from "react";
import {
  Box,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  Text,
  useDisclosure,
} from "@hightouch/htui";
import { AgentDefinitionStatus } from "src/graphql";
import { DefinitionList } from "./definition-list";
import {
  CITATION_STATUS_DOT_COLORS,
  CITATION_STATUS_TEXT_COLORS,
  type CitationStatus,
  type ResolvedCitation,
} from "./resolved-citation";

const SEGMENT_ORDER: CitationStatus[] = [
  AgentDefinitionStatus.Assumed,
  AgentDefinitionStatus.Verified,
  "deleted",
];

// The inline pill sits inside a paragraph's 24px line box, so it is shorter and aligned to the text.
const INLINE_VARIANT_PROPS = {
  display: "inline-flex",
  height: 5,
  px: 1.5,
  gap: 1.5,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  userSelect: "none",
  // Chakra has no print pseudo prop.
  sx: { "@media print": { display: "none" } },
} as const;

type Props = {
  citations: ResolvedCitation[];
  onClick: () => void;
  onSelectCitation: (citation: ResolvedCitation) => void;
  variant?: "action-bar" | "inline";
};

export const CitationsPill = ({
  citations,
  onClick,
  onSelectCitation,
  variant = "action-bar",
}: Props) => {
  const popover = useDisclosure();
  // Set by a click so the popover's pending hover-open can't reopen it over the drawer.
  // Cleared on focus rather than blur, since opening the drawer moves focus off the pill.
  const isOpenSuppressedRef = useRef(false);
  const statuses = citations.map((citation) => citation.status);
  const segments = SEGMENT_ORDER.map((segmentStatus) => ({
    status: segmentStatus,
    count: statuses.filter((status) => status === segmentStatus).length,
  })).filter((segment) => segment.count > 0);
  if (segments.length === 0) return null;

  const label = segments
    .map(({ count, status }) => `${count} ${status}`)
    .join(", ");

  return (
    <Popover
      trigger="hover"
      isOpen={popover.isOpen}
      onOpen={() => {
        if (!isOpenSuppressedRef.current) popover.onOpen();
      }}
      onClose={popover.onClose}
      placement="top-start"
      isLazy
      lazyBehavior="unmount"
    >
      <PopoverTrigger>
        <Row
          as="button"
          type="button"
          onClick={() => {
            isOpenSuppressedRef.current = true;
            popover.onClose();
            onClick();
          }}
          onMouseLeave={() => {
            isOpenSuppressedRef.current = false;
          }}
          onFocus={() => {
            isOpenSuppressedRef.current = false;
          }}
          aria-label={`View definitions: ${label}`}
          align="center"
          gap={2}
          height={6}
          px={2}
          borderRadius="full"
          borderWidth="hairline"
          borderColor="base.border"
          bg="white"
          transitionProperty="background-color, border-color"
          transitionDuration="faster"
          transitionTimingFunction="easeOutQuad"
          _hover={{
            bg: "base.lightBackground",
            borderColor: "base.borderHover",
          }}
          _focusVisible={{ boxShadow: "outline", outline: "none" }}
          {...(variant === "inline" ? INLINE_VARIANT_PROPS : {})}
        >
          {segments.map(({ status, count }, index) => (
            <Fragment key={status}>
              {index > 0 && (
                <Box
                  as="span"
                  alignSelf="stretch"
                  my={1}
                  borderLeftWidth="hairline"
                  borderColor="base.border"
                />
              )}
              <Row as="span" align="center" gap={1}>
                <Box
                  as="span"
                  boxSize={1.5}
                  borderRadius="full"
                  bg={CITATION_STATUS_DOT_COLORS[status]}
                />
                <Text
                  size="sm"
                  fontWeight="medium"
                  color={CITATION_STATUS_TEXT_COLORS[status]}
                >
                  {count}
                </Text>
              </Row>
            </Fragment>
          ))}
        </Row>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="sm"
          // height is the same as the 'sm' width of the popover content, capped to 60vh.
          maxHeight="min(var(--chakra-sizes-sm), 60vh)"
          overflowY="auto"
          py={3}
        >
          <DefinitionList
            citations={citations}
            onSelect={(citation) => {
              popover.onClose();
              onSelectCitation(citation);
            }}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
